/**
 * Sign-in. One field, one button, no password — and never a password.
 *
 * D5: email link. D7: personal accounts, so this is the person's own address
 * even on a shared tablet.
 */

import { useEffect, useState } from 'react';

import { completeLink, isPendingLink, pendingEmail, sendLink } from './data/auth.js';

type State =
  | { readonly kind: 'asking' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly email: string }
  | { readonly kind: 'completing' }
  | { readonly kind: 'error'; readonly message: string };

export function SignIn(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>({ kind: 'asking' });

  /**
   * True when this browser is holding a live sign-in link but does not know
   * which address asked for it.
   *
   * This is the ordinary case on a phone, not an edge case: the link is
   * requested inside the installed PWA, which has its own localStorage, and
   * Mail opens it in the default browser, which has none of it. Without saying
   * so the screen is an unexplained sign-in form and reads as a rejection —
   * which is half of what BUG-1 looked like.
   */
  const [linkNeedsEmail, setLinkNeedsEmail] = useState(false);

  useEffect(() => {
    // Every await in here is wrapped, because an unhandled rejection in an
    // effect is invisible: the screen simply stays on the sign-in form and no
    // amount of retrying changes anything. That cost an hour once.
    void (async () => {
      try {
        if (!(await isPendingLink())) return;

        const known = pendingEmail();
        if (known === null) {
          setLinkNeedsEmail(true);
          setState({ kind: 'asking' });
          return;
        }

        setState({ kind: 'completing' });
        await completeLink(known);
      } catch (error) {
        setState({ kind: 'error', message: describe(error) });
      }
    })();
  }, []);

  async function onSubmit(submitted: React.FormEvent): Promise<void> {
    submitted.preventDefault();
    if (email.trim() === '') return;

    try {
      // Opening a link in another browser lands here with the link still in
      // the URL: the address just typed is what completes it.
      if (await isPendingLink()) {
        setState({ kind: 'completing' });
        await completeLink(email);
        return;
      }

      setState({ kind: 'sending' });
      await sendLink(email);
      setState({ kind: 'sent', email: email.trim() });
    } catch (error) {
      setState({ kind: 'error', message: describe(error) });
    }
  }

  if (state.kind === 'completing') {
    return <p>Signing you in…</p>;
  }

  if (state.kind === 'sent') {
    return (
      <section>
        <h1>Check your email</h1>
        <p>
          A sign-in link is on its way to <strong>{state.email}</strong>. Open it on this device.
        </p>
        <button type="button" onClick={() => setState({ kind: 'asking' })}>
          Use a different address
        </button>
      </section>
    );
  }

  return (
    <section>
      <h1>{linkNeedsEmail ? 'Almost in' : 'Cairn'}</h1>
      {linkNeedsEmail ? (
        <p>
          Your sign-in link opened in a different browser from the one that asked for it, so we
          need the address again to finish. Type it and you are in — this is not a rejection.
        </p>
      ) : null}
      <form onSubmit={(e) => void onSubmit(e)}>
        <label htmlFor="email">Email address</label>
        <input
          id="email"
          type="email"
          value={email}
          autoComplete="email"
          inputMode="email"
          required
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" disabled={state.kind === 'sending'}>
          {state.kind === 'sending'
            ? 'Sending…'
            : linkNeedsEmail
              ? 'Finish signing in'
              : 'Send sign-in link'}
        </button>
      </form>
      {state.kind === 'error' ? <p role="alert">{state.message}</p> : null}
      <p>No password. We email you a link that signs you in.</p>
    </section>
  );
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
